/*
  STL CARD SLIDER — MINKPINK, 23 Sep 2026. Loaded once by sections/stl-site-assets.liquid.

  Replaces the <script> that snippets/card-product.liquid used to emit inside the product loop
  (48 identical copies on a full collection page, and never run at all for cards added later by
  Load More / filters / search, because a script injected as HTML does not execute).

  1. Arrow clicks — one delegated listener, so it covers every card on the page, now and later.
  2. Card videos — played only while the slide is actually scrolled into view inside its own
     card, and paused again on the way out. The markup is preload="none", so a video that is
     never swiped to is never downloaded.
*/
(function () {
  'use strict';

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.card-arrow');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    var wrap = btn.closest('.card-slider-wrapper');
    var track = wrap && wrap.querySelector('.media-scroll-container');
    if (!track) return;
    var dir = btn.classList.contains('card-arrow-prev') ? -1 : 1;
    track.scrollBy({ left: dir * track.clientWidth, behavior: 'smooth' });
  });

  // A card video sits in a horizontal scroller, so "on screen" is about the scroller, not the
  // page: the observer's root is the card's own track and it fires as the visitor swipes to it.
  /*
    Liquid only ever exposes the 1080p / 7.2Mbps rendition (Shopify's own video_tag on the PDP
    does the same), which is far more than a ~350px card needs. Shopify encodes lighter ones at
    predictable URLs, so try those first and keep the original as the last resort — if Shopify
    ever renames a rendition the card just plays the 1080p file, as it did before.

    The swap happens at PLAY time, by setting .src. It must NOT be done by inserting <source>
    elements and calling load(): load() fetches the video immediately EVEN WITH preload="none"
    (measured — one request, readyState 4, with no play() anywhere), which would download every
    card video on page load. Leaving the element alone until play() keeps that at zero.
  */
  /*
    Rendition ladder. Shopify names these by HEIGHT, so the usable width depends on the video's
    shape — for the portrait clips here: 480p ≈ 270px, 720p ≈ 406px, 1080p ≈ 610px wide (measured).
    A card is ~353 CSS px on a 4-up desktop grid, which is ~706 device px at 2x, so the 480p file
    was being blown up nearly 3x and looked soft on a large screen (Serena, 23 Sep).

    Pick the smallest rendition that covers the card as rendered, and fall through to the larger
    ones if a URL ever 404s. The 0.75 allowance keeps phones on the small file: a 178px card wants
    ~356 device px and 480p gives 270, which is a quarter short — invisible in motion, and it saves
    the shopper several MB over a scroll.
  */
  var LADDER = [
    { name: 'SD-480p-1.5Mbps', height: 480 },
    { name: 'HD-720p-4.5Mbps', height: 720 },
    { name: 'HD-1080p-7.2Mbps', height: 1080 }
  ];
  var HD = /HD-1080p-[\d.]+Mbps/;
  var ALLOWANCE = 0.75;

  function candidates(video) {
    var original = video.querySelector('source');
    var url = original && original.getAttribute('src');
    if (!url) return [];
    if (!HD.test(url)) return [url];

    // Poster dimensions are on the element, so the shape is known without loading anything.
    var ratio = (video.width && video.height) ? (video.width / video.height) : 0.5657;
    var box = video.getBoundingClientRect().width || video.parentElement.getBoundingClientRect().width;
    var need = box * Math.min(window.devicePixelRatio || 1, 2) * ALLOWANCE;

    var from = LADDER.findIndex(function (r) { return r.height * ratio >= need; });
    if (from < 0) from = LADDER.length - 1;                 // nothing is big enough: take the best
    return LADDER.slice(from).map(function (r) { return url.replace(HD, r.name); });
  }

  /*
    Which slide is showing is decided from the track's own scroll position, NOT from an
    IntersectionObserver alone: measured on a 390px viewport with the video slide fully in view
    (overlap 1.0), the observer never fired, and Serena reported card videos not playing on a
    phone. A scroll listener is also a user-gesture context, which keeps mobile autoplay policies
    happy. The observer is kept as a second trigger for the case where a card is rendered with a
    video already in view and nothing is ever scrolled.
  */
  function playLightest(video) {
    if (video.dataset.stlSrc) {                    // already pointed at a rendition: just resume
      var resumed = video.play();
      if (resumed && resumed.catch) resumed.catch(function () {});
      return;
    }
    var queue = candidates(video);
    if (!queue.length) return;
    video.dataset.stlSrc = '1';
    var tryNext = function () {
      if (!queue.length) return;
      video.src = queue.shift();                   // resource selection starts here, not before
      var p = video.play();
      if (p && p.catch) p.catch(function () {});   // autoplay refused: the poster simply stays
    };
    video.addEventListener('error', tryNext);      // a missing rendition falls through to the next
    tryNext();
  }

  function update(track) {
    var videos = track.querySelectorAll('.stl-card-video');
    if (!videos.length) return;
    var box = track.getBoundingClientRect();
    var mid = box.left + box.width / 2;
    videos.forEach(function (video) {
      var r = video.getBoundingClientRect();
      var showing = r.left <= mid && r.right >= mid;          // this slide is the one on screen
      if (showing) playLightest(video);
      else if (!video.paused) video.pause();
    });
  }

  function watchTrack(track) {
    if (track.dataset.stlTrack) return;
    track.dataset.stlTrack = '1';
    /*
      Throttled with a TIMER, not requestAnimationFrame: rAF is suspended in a background tab
      (and in the automated pane), which left the swipe doing nothing at all. update() runs
      straight away so the swap is immediate, plus a trailing call once the scroll settles.
    */
    var trailing = null;
    var onScroll = function () {
      update(track);
      clearTimeout(trailing);
      trailing = setTimeout(function () { update(track); }, 120);
    };
    track.addEventListener('scroll', onScroll, { passive: true });
    track.addEventListener('touchend', onScroll, { passive: true });
    update(track);                                            // a video could be the first slide
  }

  function watch(video) {
    var track = video.closest('.media-scroll-container');
    if (!track) return;
    watchTrack(track);
    if (video.dataset.stlWatched) return;
    video.dataset.stlWatched = '1';
    new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) playLightest(video);        // autoplay refused: the poster stays
        else if (!video.paused) video.pause();
      });
    }, { root: track, threshold: 0.6 }).observe(video);
  }

  function scan(root) {
    (root || document).querySelectorAll('.stl-card-video').forEach(watch);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { scan(); });
  else scan();

  // Load More, a filter change and the search drawer all inject cards after load.
  new MutationObserver(function (muts) {
    for (var i = 0; i < muts.length; i++) {
      if (muts[i].addedNodes.length) { scan(); return; }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
